import {
  GameOnboardingPopupDoc,
  GameOnboardingPopupId,
} from 'src/app/interfaces/game-onboarding-popup.interface';

/** Local Sprites fallbacks when Firestore image keys are missing. */
export const ONBOARDING_SPRITE = {
  header: '/assets/game_page/Sprites/popup-1-icon.png',
  headerPrizeBox: '/assets/game_page/Sprites/prizebox-large.png',
  headerUnlock: '/assets/game_page/Sprites/popup-3-icon.png',
  ticket: '/assets/game_page/Sprites/prize-token-small.png',
  ticketLarge: '/assets/game_page/Sprites/prize-token-large.png',
  prizeBox: '/assets/game_page/Sprites/prize-box-small.png',
  prizeBoxLarge: '/assets/game_page/Sprites/prize-box-large.png',
  chest: '/assets/game_page/Sprites/chest.png',
  instantWin: '/assets/game_page/Sprites/intant-win-games.png',
  /** 3D cash stack for the yellow prize card (no dark sprite plate). */
  cashCredit: '/assets/game_page/pannel/credit.svg',
  cashAndCredit: '/assets/game_page/Sprites/cash-and-credit.png',
  creditLarge: '/assets/game_page/Sprites/credit-large.png',
  patternJoystick: '/assets/game_page/Sprites/container-short-joystick.png',
  patternKeys: '/assets/game_page/Sprites/container-long-keys.png',
} as const;

/** Fallback image keys by document `type` when explicit banner/row icons are missing. */
const TYPE_ICON_FALLBACK: Record<
  string,
  { banner?: string; row1: string; row2: string; row3?: string }
> = {
  challenge_intro: {
    row1: 'instant_win_games',
    row2: 'prize_box',
    row3: 'cash_and_credit',
  },
  challenge_completed: {
    banner: 'ticket',
    row1: 'prize_box',
    row2: 'prize_box_large',
  },
  prize_unlocker_credit_added: {
    banner: 'banner_icon',
    row1: 'prize_box_large',
    row2: 'treasure_chest',
  },
  prize_box_opened: {
    banner: 'banner_icon',
    row1: 'prize_box_large',
    row2: 'treasure_chest',
  },
  prizes_unlocked: {
    banner: 'banner_icon',
    row1: 'instant_win_games',
    row2: 'cash_and_credit',
  },
  prize_unlocked: {
    banner: 'banner_icon',
    row1: 'instant_win_games',
    row2: 'cash_and_credit',
  },
};

const SPRITE_BY_KEY: Record<string, string> = {
  ticket: ONBOARDING_SPRITE.ticket,
  prize_box: ONBOARDING_SPRITE.prizeBox,
  prize_box_large: ONBOARDING_SPRITE.prizeBoxLarge,
  treasure_chest: ONBOARDING_SPRITE.chest,
  banner_icon: ONBOARDING_SPRITE.ticketLarge,
  instant_win_games: ONBOARDING_SPRITE.instantWin,
  cash_and_credit: ONBOARDING_SPRITE.cashAndCredit,
  header_icon: ONBOARDING_SPRITE.header,
};

const CANONICAL_POPUP_IDS: readonly GameOnboardingPopupId[] = [
  'challenge_intro',
  'challenge_completed',
  'prize_box_opened',
  'prize_unlocked',
];

/** Legacy / seed aliases → FE popup ids used by GameOnboardingPopupService. */
const POPUP_ID_ALIASES: Record<string, GameOnboardingPopupId> = {
  challenge_intro: 'challenge_intro',
  challenge_completed: 'challenge_completed',
  prize_box_opened: 'prize_box_opened',
  prize_unlocked: 'prize_unlocked',
  // Seed / Lucid names that must map to FE docs
  challenge_initial_popup: 'challenge_intro',
  prize_box_gained: 'prize_box_opened',
  you_gained_a_prize: 'prize_box_opened',
  you_unlocked_your_prizes: 'prize_unlocked',
  // Legacy CMS / backend document ids
  challenge_popup: 'challenge_completed',
  prizebox_popup: 'prize_box_opened',
  prizeunlocker_popup: 'prize_unlocked',
};

/** Normalize engine/seed popup_id to a FE-supported id, or null if unknown. */
export function normalizeGameOnboardingPopupId(
  id: string | null | undefined
): GameOnboardingPopupId | null {
  if (!id) {
    return null;
  }
  return POPUP_ID_ALIASES[String(id).trim()] ?? null;
}

/** Strict type guard — canonical FE ids only (not seed/CMS aliases). */
export function isGameOnboardingPopupId(id: string): id is GameOnboardingPopupId {
  return (CANONICAL_POPUP_IDS as readonly string[]).includes(id);
}

/**
 * Replace `{{name_icon}}` tokens with inline `<img>` tags from `images[name_inline]`.
 * Text is HTML-escaped; only allowlisted image URLs are emitted.
 */
export function renderPopupLine(
  raw: string,
  images: Record<string, string | undefined>
): string {
  if (!raw) {
    return '';
  }
  const tokenRe = /\{\{\s*([a-z0-9_]+?)_icon\s*\}\}/gi;
  let result = '';
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = tokenRe.exec(raw)) !== null) {
    result += escapeHtmlPreservingSafeTags(raw.slice(lastIndex, match.index));
    const src = sanitizeImageUrl(images[`${match[1]}_inline`]);
    if (src) {
      const safeName = escapeHtml(String(match[1]));
      result += `<img class="op-inline-icon" src="${src}" alt="${safeName}" style="height:16px;width:auto;max-width:22px;vertical-align:-0.25em;display:inline-block;object-fit:contain;">`;
    }
    lastIndex = match.index + match[0].length;
  }
  result += escapeHtmlPreservingSafeTags(raw.slice(lastIndex));
  return result;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape HTML but keep CMS-safe emphasis tags from Firestore popup copy
 * (`<b>`, `<i>`, `<strong>`, `<em>`, `<u>`, `<br>`).
 */
function escapeHtmlPreservingSafeTags(value: string): string {
  return escapeHtml(value)
    .replace(/&lt;(\/?)(b|i|strong|em|u)\s*&gt;/gi, '<$1$2>')
    .replace(/&lt;br\s*\/?\s*&gt;/gi, '<br>');
}

function sanitizeImageUrl(url?: string): string {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('/assets/') || /^https:\/\//i.test(trimmed)) {
    return trimmed.replace(/["'<>\\]/g, '');
  }
  return '';
}

/** Strip unresolved `[currency_beautify:N]` tokens after a failed beautify call. */
export function stripCurrencyBeautifyTokens(raw: string): string {
  if (!raw) {
    return '';
  }
  return raw.replace(/\[currency_beautify:[^\]]*\]/gi, '').trim();
}

/**
 * Last-resort currency token fallback: keep the numeric amount so banner
 * prize_value still renders when getDVFromString times out / fails.
 * `[currency_beautify:100]` → `100`
 */
export function fallbackCurrencyBeautifyValue(raw: string): string {
  if (!raw) {
    return '';
  }
  return raw
    .replace(/\[currency_beautify:\s*([^\]]+?)\s*\]/gi, (_, amount: string) =>
      String(amount).trim()
    )
    .trim();
}

function resolveImage(
  images: Record<string, string | undefined>,
  key?: string
): string | undefined {
  if (!key) {
    return undefined;
  }
  return images[key] || SPRITE_BY_KEY[key];
}

/** CMS `{{name_icon}}` tokens read `images[name_inline]`. Alias sibling keys. */
export function withPopupInlineImages(
  images: Record<string, string | undefined> | null | undefined
): Record<string, string | undefined> {
  const src = images ?? {};
  return {
    ...src,
    ticket_inline: src.ticket_inline || src.ticket,
    cash_inline: src.cash_inline || src.cash_and_credit,
    credit_inline: src.credit_inline || src.cash_and_credit,
    // CMS node next to "£100!" in prize_box_opened body copy
    more_cash_inline:
      src.more_cash_inline || src.cash_inline || src.cash_and_credit,
  };
}

export function resolveBannerIcon(
  doc: GameOnboardingPopupDoc
): string | undefined {
  const images = doc.images ?? {};
  if (
    doc.type === 'prize_unlocker_credit_added' ||
    doc.type === 'prize_box_opened'
  ) {
    // Yellow-card credit art comes from CMS `banner_icon` (not cash_inline).
    return (
      images.banner_icon ||
      images.cash_inline ||
      images.cash_and_credit ||
      ONBOARDING_SPRITE.cashAndCredit
    );
  }
  if (images.banner_icon) {
    return images.banner_icon;
  }
  const fallbackKey = TYPE_ICON_FALLBACK[doc.type]?.banner;
  return resolveImage(images, fallbackKey);
}

export function resolveRowIcons(
  doc: GameOnboardingPopupDoc
): [string | undefined, string | undefined, string | undefined] {
  const images = doc.images ?? {};
  const fallback = TYPE_ICON_FALLBACK[doc.type];

  const row1 =
    images.line_1_icon ||
    resolveImage(images, fallback?.row1);
  const row2 =
    images.line_2_icon ||
    resolveImage(images, fallback?.row2);
  const row3 =
    images.line_3_icon ||
    resolveImage(images, fallback?.row3);

  return [row1, row2, row3];
}

export function resolveHeaderIcon(
  doc: GameOnboardingPopupDoc
): string | undefined {
  const images = doc.images ?? {};
  if (images.header_icon) {
    return images.header_icon;
  }
  if (doc.type === 'challenge_intro') {
    return ONBOARDING_SPRITE.header;
  }
  if (
    doc.type === 'prize_unlocker_credit_added' ||
    doc.type === 'prize_box_opened'
  ) {
    return ONBOARDING_SPRITE.headerPrizeBox;
  }
  if (doc.type === 'prizes_unlocked' || doc.type === 'prize_unlocked') {
    return ONBOARDING_SPRITE.headerUnlock;
  }
  return ONBOARDING_SPRITE.header;
}

export function pickPopupContent(
  doc: GameOnboardingPopupDoc,
  lang: string
): GameOnboardingPopupDoc['content'][string] | undefined {
  const content = doc.content ?? {};
  const normalized = (lang || 'en').trim();
  const base = normalized.split('-')[0] || 'en';
  return content[normalized] ?? content[base] ?? content['en'];
}
